import { Box, FormControlLabel, FormGroup, Grid, Switch, TextField } from '@mui/material'
import { Dayjs } from 'dayjs'
import { ReactComponent as AccessibleIcon } from '../../../assets/icons/date-picker.svg'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider/LocalizationProvider'
import { useContext } from 'react'
import { GlobalInfoContext } from 'contexts/GlobalStateContext'
import { FormikTouched, FormikValues } from 'formik'
import Item from 'pages/dashboard/sub-sections/items'

export type Orgs = {
  label: string
  businessId: string
  country?: string
  displayId: string
  type: string
  commonName?: string | undefined
  legalEntityCodes?: [
    {
      legalEntityCodeType?: string
      legalEntityCodeData?: string
    }
  ]
  contactFirstName?: string | undefined
  contactLastName?: string | undefined
  address?: string | undefined
  phone?: string | undefined
  email?: string | undefined
}

type TimeRowProps = {
  startDateValue: Dayjs | null
  endDateValue: Dayjs | null
  maxDate: Dayjs | null
  minDate: Dayjs | null
  setDateValue: (value: any, type: string) => any
  openEnded: boolean
  setOpenEnded: any
  errors?: FormikValues
  touched?: FormikTouched<any>
  disablePast?: boolean
}
export const TimeRow = ({
  startDateValue,
  endDateValue,
  minDate,
  maxDate,
  setDateValue,
  errors,
  touched,
  disablePast
}: TimeRowProps) => {
  const { purpose } = useContext(GlobalInfoContext)

  return (
    <>
      <Grid item xs={3.94}>
        <Item>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Contract Start Date"
              value={purpose?.contract?.startDate || startDateValue}
              inputFormat="DD/MM/YYYY"
              onChange={(newValue) => setDateValue(newValue, 'startDate')}
              disablePast={disablePast ?? false}
              maxDate={maxDate}
              renderInput={(params) => (
                <TextField
                  label="Contract Start Date"
                  {...params}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, readOnly: true }}
                  error={Boolean(touched?.startDate && errors?.startDate)}
                  helperText={touched?.startDate && errors?.startDate}
                />
              )}
              components={{
                OpenPickerIcon: AccessibleIcon
              }}
            />
          </LocalizationProvider>
        </Item>
      </Grid>
      <Grid item xs={1} />
      <Grid item xs={4.06}>
        <Item>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Contract End Date"
              value={purpose?.contract?.endDate || endDateValue || null}
              inputFormat="DD/MM/YYYY"
              onChange={(newValue) => setDateValue(newValue, 'endDate')}
              minDate={minDate}
              disablePast={disablePast ?? false}
              components={{
                OpenPickerIcon: AccessibleIcon
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contract End Date"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ ...params.inputProps, readOnly: true }}
                  error={Boolean(touched?.endDate && errors?.endDate)}
                  helperText={touched?.endDate && errors?.endDate}
                />
              )}
            />
          </LocalizationProvider>
        </Item>
      </Grid>
    </>
  )
}

export const ExpiryDate = ({
  expiryDateValue,
  setExpiryDate,
  openEnded,
  handleOpenEndedDate,
  errors,
  touched
}) => {
  const { expiryDate } = useContext(GlobalInfoContext)
  const value = expiryDate ? expiryDate : expiryDateValue
  return (
    <>
      <Grid item xs={4} sx={{ paddingRight: '15px' }}>
        <Item>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Expiry Date (Mandatory)"
              value={value}
              data-testid="picker-expiry-date"
              disabled={openEnded}
              inputFormat="DD/MM/YYYY"
              disablePast={true}
              onChange={(newValue) => setExpiryDate(newValue, 'expiryDate')}
              renderInput={(params) => (
                <TextField
                  label="Expiry Date"
                  {...params}
                  id="expiry-date"
                  placeholder="Expiry Date (Mandatory)"
                  InputLabelProps={{ shrink: true }}
                  error={Boolean(touched?.expiryDate && errors?.expiryDate)}
                  helperText={touched?.expiryDate && errors?.expiryDate}
                  inputProps={{ ...params.inputProps, readOnly: true }}
                />
              )}
              components={{
                OpenPickerIcon: AccessibleIcon
              }}
            />
          </LocalizationProvider>
        </Item>
      </Grid>
      <Grid item md={1} lg={1} />
      <Grid item md={2} lg={2}>
        <Box>
          <FormGroup>
            <FormControlLabel
              id="no-expiry"
              control={
                <Switch checked={openEnded} onChange={() => handleOpenEndedDate(!openEnded)} />
              }
              label="No Expiry Date"
            />
          </FormGroup>
        </Box>
      </Grid>
    </>
  )
}
